import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import {
 ArrowRight,
 Gift,
 Users,
 Crown,
 Heart,
 Sparkles,
 Share2,
 Store,
 Trophy,
 ShieldCheck,
 LogIn,
 Check,
 Zap,
 Wallet,
 Lock,
 Gem,
 Shield,
 Coins,
 TrendingUp,
 Award,
 Receipt,
 DollarSign,
 Tag,
 Instagram,
 Facebook,
 Twitter,
 MessageCircle,
 BadgeCheck,
 ClipboardList,
 Star,
 X,
 Zap as ZapIcon,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { LanguageProvider } from '../context/LanguageContext';
import { useAmbassador } from '../context/AmbassadorContext';
import CountUp from '../components/CountUp';
import CustomerAuthModal from '../components/CustomerAuthModal';

/**
 * NoFee Rewards Hub — premium dark-mode conversion-focused landing page.
 *
 * Designed against /app/design_guidelines.json (2026-02-26 redesign).
 *
 * Sections (mobile-first ordering for conversion):
 * 1. Hero — bold headline, primary CTA above-the-fold, gold accent
 * 2. Trust Strip — Stripe/cancel anytime/fast payouts
 * 3. Social Proof — CountUp stats (ambassadors / restaurants / rewards)
 * 4. How It Works — 3 steps, simple icons
 * 5. Benefits — 5 cards (bento-style)
 * 6. Tiers — Bronze/Gold/Elite/Founding aspirational ladder
 * 7. Final CTA + Sticky Mobile CTA bar (after 50% scroll)
 *
 * Functional guarantees (preserved from previous version):
 * - Uses `useAmbassador` context
 * - Reuses `AmbassadorAuthModal` as-is
 * - Deep links:?auth=login,?auth=signup, #how
 * - Global Navbar (hamburger drawer) instead of inline back-button
 */

const STEPS = [
 {
 icon: Share2,
 n: '01',
 title: 'Invite restaurants',
 desc: 'Share your personal NoFee code with restaurants and friends in your city.',
 },
 {
 icon: Store,
 n: '02',
 title: 'Restaurants join',
 desc: 'When they sign up, the referral is tracked automatically — no follow-up needed.',
 },
 {
 icon: Gift,
 n: '03',
 title: 'Earn rewards',
 desc: 'Get gift credits, climb VIP tiers, and watch local kitchens keep 100% of every order.',
 },
];

const BENEFITS = [
 // First 3 are the headline cards — each has a `modal` payload that
 // opens a full Learn More dialog. Visually accented with the gold
 // border to draw the customer's eye in the first 3 seconds.
 {
 key: 'pay-less',
 icon: DollarSign,
 emoji: '💰',
 title: 'Pay Less For Your Food',
 desc: "Many restaurants can offer better prices with NoFee because they don't pay high marketplace commissions.",
 featured: true,
 modal: {
 title: 'Pay Less For Your Food',
 intro: 'Many restaurants increase menu prices on traditional delivery apps to offset high marketplace commissions.\n\nWith NoFee, participating restaurants keep more of their revenue and may offer more competitive pricing directly to customers.',
 points: [
 'Better pricing opportunities',
 'More money stays with local restaurants',
 'Direct restaurant ordering',
 'No marketplace commission pressure',
 ],
 },
 },
 {
 key: 'fast',
 icon: Zap,
 emoji: '⚡',
 title: 'Fast by NoFee',
 desc: 'Delivered On Time. Verified by Real Customers. See which restaurants consistently deliver within their promised time.',
 featured: true,
 modal: {
 title: 'Fast by NoFee',
 subtitle: 'Delivered On Time. Verified by Real Customers.',
 intro: 'Restaurants choose their delivery time.\n\nCustomers confirm whether their order arrived within that promised time.\n\nFast by NoFee helps customers identify restaurants with strong delivery performance.',
 points: [
 'Real customer verification',
 'On-time delivery tracking',
 'Improve customer trust',
 'Reliable delivery recognition',
 'Greater confidence when ordering',
 ],
 },
 },
 {
 key: 'rewards',
 icon: Gift,
 emoji: '🎁',
 title: 'Rewards & Gift Credits',
 desc: 'Earn points, redeem rewards, unlock VIP benefits, and enjoy exclusive offers.',
 featured: true,
 modal: {
 title: 'Rewards & Gift Credits',
 intro: 'Create a free account and start earning rewards every time you order.',
 points: [
 'Earn Reward Points',
 'Redeem Gift Credits',
 'Referral Rewards',
 'VIP Rewards',
 'Exclusive Promotions',
 'Faster Checkout',
 ],
 },
 },
 // The remaining 5 are simple informational cards — no modal, no
 // "Learn More" CTA. They communicate value at a glance.
 { key: 'favorites', icon: Heart, emoji: '❤️', title: 'Save Favorite Restaurants', desc: 'Quick access to your favorite places.' },
 { key: 'history', icon: ClipboardList, emoji: '📋', title: 'Order History', desc: 'Reorder previous meals with one click.' },
 { key: 'checkout', icon: ZapIcon, emoji: '⚡', title: 'Faster Checkout', desc: 'Save time on every future order.' },
 { key: 'referrals', icon: Users, emoji: '👥', title: 'Referral Rewards', desc: 'Invite friends and earn additional rewards.' },
 { key: 'vip', icon: Star, emoji: '⭐', title: 'VIP Rewards', desc: 'Unlock premium benefits as your activity grows.' },
];

const TRUST_BADGES = [
 { icon: Zap, label: 'Direct Stripe payouts' },
 { icon: Lock, label: 'Powered by Stripe' },
 { icon: Check, label: 'Cancel anytime' },
 { icon: ShieldCheck, label: 'No hidden fees' },
];

/* ───────────────────────── Sticky mobile CTA ───────────────────────── */
function StickyMobileCTA({ onJoin, visible }) {
 return (
 <div
 data-testid="mobile-sticky-cta"
 className={`md:hidden fixed bottom-4 left-4 right-4 z-[70] transition-all duration-300 ${
 visible? 'translate-y-0 opacity-100': 'translate-y-6 opacity-0 pointer-events-none'
 }`}
 style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
 >
 <div className="flex items-center justify-between gap-3 p-3 rounded-2xl backdrop-blur-xl bg-[#121212]/95 border border-yellow-500/20 shadow-[0_-8px_30px_rgba(0,0,0,0.7)]">
 <div className="flex items-center gap-2.5 pl-1.5">
 <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center shadow-[0_4px_14px_-3px_rgba(250,204,21,0.6)]">
 <Sparkles className="w-4 h-4 text-black" />
 </div>
 <div className="leading-tight">
 <div className="text-[11px] uppercase tracking-[0.18em] text-yellow-300/90 font-bold">
 Free · No card
 </div>
 <div className="text-[13px] text-white font-bold">Earn from referrals</div>
 </div>
 </div>
 <button
 type="button"
 onClick={onJoin}
 data-testid="mobile-sticky-cta-button"
 className="inline-flex items-center gap-1.5 px-5 min-h-[52px] rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black text-[13px] font-extrabold transition-all active:scale-95 shadow-[0_8px_20px_-6px_rgba(250,204,21,0.5)]"
 >
 Join <ArrowRight className="w-3.5 h-3.5" />
 </button>
 </div>
 </div>
 );
}

/* ───────────────────────── main ───────────────────────── */
const RewardsLandingInner = () => {
 const { ambassador } = useAmbassador();
 const [params, setParams] = useSearchParams();
 // Compute the initial deep-link auth mode synchronously from window.location.
 // This guards against any upstream rewrite/redirect that may strip the query
 // string before React's useSearchParams hook resolves on the first render.
 const initialAuthFromURL = (() => {
 try {
 if (typeof window === 'undefined') return null;
 const qs = new URLSearchParams(window.location.search);
 const q = qs.get('auth');
 if (q === 'login' || q === 'signup') return q;
 } catch {
 /* ignore */
 }
 return null;
 })();

 const [authOpen, setAuthOpen] = useState(Boolean(initialAuthFromURL));
 const [authMode, setAuthMode] = useState(initialAuthFromURL || 'signup');
 const [stickyVisible, setStickyVisible] = useState(false);
 // Which benefit's Learn More modal is currently open (by `key`).
 // `null` means no modal showing.
 const [benefitModalKey, setBenefitModalKey] = useState(null);

 // Lock background scroll while ANY modal (benefit Learn More or Customer
 // Auth) is open — and restore on close. Otherwise the page underneath
 // scrolls and the centered modal can appear cut at the top on tablets.
 useEffect(() => {
 const anyModalOpen = Boolean(benefitModalKey) || authOpen;
 if (!anyModalOpen) return undefined;
 const prevOverflow = document.body.style.overflow;
 document.body.style.overflow = 'hidden';
 return () => { document.body.style.overflow = prevOverflow; };
 }, [benefitModalKey, authOpen]);

 // One-account rule: if a NoFee customer is already authenticated in this
 // browser, do NOT push them through a second signup flow. We surface a
 // single "Continue to my Rewards" CTA at the bottom of the page that routes
 // them straight to /customer/rewards (their loyalty dashboard).
 const customerToken =
 typeof window!== 'undefined'? localStorage.getItem('nofee_customer_token'): null;
 const isCustomerLoggedIn = Boolean(customerToken);

 // Deep-link support: /rewards?auth=login or?auth=signup
 useEffect(() => {
 const q = params.get('auth');
 if (q === 'login' || q === 'signup') {
 setAuthMode(q);
 setAuthOpen(true);
 }
 }, [params]);

 // Hash deep-link: /rewards#how → smooth-scroll
 useEffect(() => {
 const hash = window.location.hash;
 if (hash) {
 const t = setTimeout(() => {
 const el = document.querySelector(hash);
 if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
 }, 80);
 return () => clearTimeout(t);
 }
 }, []);

 // Sticky mobile CTA — appears only deep in the page (≥75% scrolled), so it
 // never competes with the educational content above. Hidden completely if
 // user already has an account (ambassador OR customer).
 useEffect(() => {
 if (ambassador || isCustomerLoggedIn) return;
 const cookieResolved = () => {
 try {
 return Boolean(localStorage.getItem('nofee_cookie_consent'));
 } catch {
 return true;
 }
 };
 const onScroll = () => {
 const doc = document.documentElement;
 const scrollable = doc.scrollHeight - window.innerHeight;
 const pct = scrollable > 0? window.scrollY / scrollable: 0;
 setStickyVisible(pct >= 0.75 && cookieResolved());
 };
 const onStorage = () => onScroll();
 window.addEventListener('scroll', onScroll, { passive: true });
 window.addEventListener('storage', onStorage);
 const poll = setInterval(onScroll, 500);
 onScroll();
 return () => {
 window.removeEventListener('scroll', onScroll);
 window.removeEventListener('storage', onStorage);
 clearInterval(poll);
 };
 }, [ambassador, isCustomerLoggedIn]);

 const openAuth = (mode) => {
 setAuthMode(mode);
 setAuthOpen(true);
 };
 const closeAuth = () => {
 setAuthOpen(false);
 if (params.get('auth')) {
 params.delete('auth');
 setParams(params, { replace: true });
 }
 };

 return (
 <div className="min-h-screen bg-[#0A0A0A] text-white selection:bg-yellow-500/30" data-testid="rewards-hub">
 {/* Global navbar (hamburger on mobile) */}
 <Navbar />

 {/* ═══════════════ 1. HERO ═══════════════ */}
 <section
 className="relative overflow-hidden pt-28 sm:pt-32 pb-20 lg:pt-36 lg:pb-28"
 data-testid="ambassador-hero"
 >
 {/* Background mesh — subtle gold radials, no flat black */}
 <div className="absolute inset-0 pointer-events-none z-0">
 <div className="absolute top-[-10%] right-[5%] w-[48rem] h-[48rem] bg-yellow-500/[0.10] rounded-full blur-[160px]" />
 <div className="absolute bottom-[-10%] left-[10%] w-[34rem] h-[34rem] bg-amber-700/[0.08] rounded-full blur-[140px]" />
 <div
 className="absolute inset-0 opacity-[0.025]"
 style={{
 backgroundImage:
 'radial-gradient(circle at 25% 30%, rgba(255,255,255,0.4) 1px, transparent 1px)',
 backgroundSize: '48px 48px',
 }}
 />
 </div>

 <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
 {/* Eyebrow */}
 <div
 className="inline-flex items-center gap-2 px-3.5 py-1.5 mb-7 rounded-full border border-yellow-500/30 bg-yellow-500/[0.06] animate-fade-in"
 data-testid="ambassador-hero-eyebrow"
 >
 <ShieldCheck className="w-3.5 h-3.5 text-yellow-400" />
 <span className="text-[10.5px] uppercase tracking-[0.24em] text-yellow-300 font-bold">
 NoFee Rewards Program
 </span>
 </div>

 {/* H1 — Outfit, premium tracking */}
 <h1
 className="font-['Outfit',_Inter,_sans-serif] text-4xl sm:text-5xl lg:text-6xl xl:text-[80px] font-semibold tracking-[-0.04em] leading-[1.02] text-white max-w-5xl mx-auto"
 data-testid="ambassador-hero-headline"
 >
 Why create a free{' '}
 <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
 NoFee
 </span>{' '}
 account?
 </h1>

 <p
 className="mt-7 text-base sm:text-lg lg:text-xl text-white/65 leading-relaxed max-w-2xl mx-auto"
 data-testid="ambassador-hero-subhead"
 >
 Save money, enjoy trusted deliveries, earn rewards, and order faster every time.
 </p>

 {/* Primary above-the-fold CTA. Respects the one-account rule:
 already-signed-in NoFee customers skip the modal and land
 directly on their private /customer/rewards dashboard. */}
 <div className="mt-10 flex flex-col items-center gap-3">
 {isCustomerLoggedIn? (
 <Link
 to="/account"
 data-testid="hero-cta-button"
 className="group inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
 >
 <Crown className="w-4 h-4" /> Go to My Account
 <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
 </Link>
 ): (
 <button
 type="button"
 onClick={() => openAuth('signup')}
 data-testid="hero-cta-button"
 className="group inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
 >
 <Sparkles className="w-4 h-4" /> Start Earning Rewards
 <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
 </button>
 )}
 <p
 className="text-[12.5px] text-white/45 tracking-wide"
 data-testid="hero-microcopy"
 >
 Free forever · No credit card · One account, full access
 </p>
 <button
 type="button"
 onClick={() => {
 document
.getElementById('benefits')
?.scrollIntoView({ behavior: 'smooth', block: 'start' });
 }}
 data-testid="hero-scroll-cue"
 aria-label="Scroll to learn more"
 className="group mt-2 inline-flex flex-col items-center gap-1 text-white/40 hover:text-yellow-400 transition-colors"
 >
 <span className="text-[10.5px] uppercase tracking-[0.24em] font-bold">
 Discover how it works
 </span>
 <ArrowRight className="w-4 h-4 rotate-90 transition-transform group-hover:translate-y-1" />
 </button>
 </div>
 </div>
 </section>

 {/* ═══════════════ 2. TRUST STRIP ═══════════════ */}
 <section
 className="w-full border-y border-white/10 bg-white/[0.02] py-7 backdrop-blur-sm"
 data-testid="trust-strip"
 >
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="flex flex-wrap items-center justify-center gap-x-7 sm:gap-x-10 gap-y-4 text-[12.5px] sm:text-[13px] text-white/55 font-medium">
 {TRUST_BADGES.map(({ icon: Icon, label }) => (
 <span
 key={label}
 className="inline-flex items-center gap-2 hover:text-white/80 transition-colors"
 data-testid={`trust-badge-${label.toLowerCase().replace(/\s+/g, '-')}`}
 >
 <Icon className="w-4 h-4 text-yellow-400/85" strokeWidth={1.8} />
 {label}
 </span>
 ))}
 </div>
 </div>
 </section>

 {/* ═══════════════ 3. SOCIAL PROOF (stats) ═══════════════ */}
 <section className="py-16 sm:py-20 border-b border-white/5" data-testid="rewards-social-proof">
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.28em] mb-3">
 Growing every day
 </p>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-tight">
 A community already earning together.
 </h2>

 <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
 {[
 { label: 'Members', value: 2500, suffix: '+', testid: 'stats-members' },
 { label: 'Restaurants joined', value: 150, suffix: '+', testid: 'stats-restaurants' },
 { label: 'Gift credits distributed', value: 12000, prefix: '$', suffix: '+', testid: 'stats-rewards' },
 ].map((s) => (
 <div
 key={s.label}
 data-testid={s.testid}
 className="relative p-7 rounded-2xl border border-white/8 bg-gradient-to-b from-white/[0.03] to-transparent hover:border-yellow-500/25 transition-colors"
 >
 <div className="font-['Outfit',_Inter,_sans-serif] text-4xl sm:text-5xl font-semibold tracking-tight bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
 {s.prefix || ''}
 <CountUp to={s.value} duration={2200} suffix={s.suffix || ''} />
 </div>
 <p className="mt-2 text-[13px] text-white/55 uppercase tracking-[0.18em] font-medium">
 {s.label}
 </p>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* ═══════════════ 4. HOW IT WORKS ═══════════════ */}
 <section
 id="how"
 className="py-20 sm:py-24 border-b border-white/5"
 data-testid="how-it-works-section"
 >
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-2xl mx-auto">
 <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.28em] mb-3">
 How it works
 </p>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-tight">
 Three steps. Real rewards.
 </h2>
 <p className="mt-4 text-[15px] text-white/55 leading-relaxed">
 Simple, transparent, and built around real local impact.
 </p>
 </div>

 <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
 {STEPS.map((s, idx) => (
 <div
 key={s.n}
 data-testid={`step-${idx + 1}`}
 className="group relative p-7 rounded-3xl border border-white/8 bg-gradient-to-br from-white/[0.03] to-transparent hover:border-yellow-500/30 hover:bg-white/[0.04] transition-all hover:-translate-y-0.5"
 >
 <div className="flex items-center justify-between mb-6">
 <div className="w-12 h-12 rounded-2xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center group-hover:bg-yellow-500/15 transition-colors">
 <s.icon className="w-5 h-5 text-yellow-400" strokeWidth={1.75} />
 </div>
 <div className="text-yellow-500/40 text-3xl font-['Outfit',_Inter,_sans-serif] font-semibold leading-none">
 {s.n}
 </div>
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-xl font-medium tracking-tight text-white">
 {s.title}
 </h3>
 <p className="mt-2 text-[13.5px] text-white/55 leading-relaxed">{s.desc}</p>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* ═══════════════ 5. BENEFITS (bento) ═══════════════ */}
 <section id="benefits" className="py-20 sm:py-24 border-b border-white/5" data-testid="benefits-grid">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="max-w-3xl">
 <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.28em] mb-3">
 Why join NoFee
 </p>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.1]">
 Save money. Trust delivery.{' '}
 <span className="text-yellow-400">Earn rewards.</span>
 </h2>
 <p className="mt-4 text-[15px] text-white/55 max-w-xl leading-relaxed">
 The three reasons our customers create a free NoFee account — plus five more perks along the way.
 </p>
 </div>

 {/* Top 3 featured cards — full-width emphasis with Learn More CTA */}
 <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
 {BENEFITS.filter((b) => b.featured).map((b) => {
 const Icon = b.icon;
 return (
 <button
 key={b.key}
 type="button"
 onClick={() => setBenefitModalKey(b.key)}
 data-testid={`benefit-${b.key}`}
 className="group text-left p-7 rounded-2xl border border-yellow-500/35 bg-gradient-to-br from-yellow-500/[0.08] via-yellow-500/[0.03] to-transparent hover:border-yellow-400/60 hover:from-yellow-500/[0.12] transition-all"
 >
 <div className="flex items-center gap-3 mb-4">
 <div className="w-12 h-12 rounded-xl bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center text-2xl">
 {b.emoji}
 </div>
 <Icon className="w-5 h-5 text-yellow-300/60" strokeWidth={1.75} />
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-lg font-semibold text-white tracking-tight leading-tight">
 {b.title}
 </h3>
 <p className="mt-2 text-[13.5px] text-white/60 leading-relaxed">{b.desc}</p>
 <span
 className="mt-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-500/15 text-yellow-200 group-hover:bg-yellow-500 group-hover:text-black text-[11.5px] font-bold transition-colors"
 data-testid={`benefit-${b.key}-learn-more`}
 >
 Learn More <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
 </span>
 </button>
 );
 })}
 </div>

 {/* Informational cards — no modal */}
 <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
 {BENEFITS.filter((b) =>!b.featured).map((b) => {
 const Icon = b.icon;
 return (
 <div
 key={b.key}
 data-testid={`benefit-${b.key}`}
 className="p-5 rounded-2xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15 transition-colors"
 >
 <div className="flex items-center gap-2 mb-3">
 <span className="text-xl" aria-hidden="true">{b.emoji}</span>
 <Icon className="w-4 h-4 text-white/40" strokeWidth={1.75} />
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-[14px] font-semibold text-white tracking-tight leading-tight">
 {b.title}
 </h3>
 <p className="mt-1.5 text-[12.5px] text-white/55 leading-relaxed">{b.desc}</p>
 </div>
 );
 })}
 </div>
 </div>
 </section>

 {/* ═══════════════ Benefit Learn More modal ═══════════════ */}
 {/* ── Benefit "Learn More" modal ──
 Rendered via `createPortal` to `document.body` so no ancestor
 `transform` / `filter` / `will-change` can break the
 `position: fixed` centering (a common cause of "modal opens
 above the viewport"). Uses explicit transform-based centering
 per product spec — `top:50%; left:50%; translate(-50%,-50%);
 max-height: 90vh; overflow-y: auto`. */}
 {benefitModalKey && (() => {
 const b = BENEFITS.find((x) => x.key === benefitModalKey);
 if (!b ||!b.modal) return null;
 return createPortal(
 <div
 className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm"
 onClick={() => setBenefitModalKey(null)}
 data-testid={`benefit-modal-${b.key}`}
 // 100dvh ensures the overlay tracks the dynamic viewport on
 // iOS Safari (where 100vh includes the URL bar and pushes
 // content off-screen).
 style={{ width: '100vw', height: '100dvh' }}
 >
 <div
 className="bg-zinc-950 border border-yellow-500/30 rounded-3xl shadow-2xl overflow-hidden flex flex-col w-[calc(100vw-32px)] sm:w-full sm:max-w-lg"
 onClick={(e) => e.stopPropagation()}
 style={{
 position: 'fixed',
 top: '50%',
 left: '50%',
 transform: 'translate(-50%, -50%)',
 maxHeight: '90vh',
 }}
 >
 <div className="relative bg-gradient-to-br from-yellow-500/15 via-amber-500/10 to-transparent p-6 sm:p-7 border-b border-white/5 flex-shrink-0">
 <button
 type="button"
 onClick={() => setBenefitModalKey(null)}
 className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors z-10"
 aria-label="Close"
 data-testid="benefit-modal-close"
 >
 <X className="w-5 h-5 text-white" />
 </button>
 <div className="inline-flex items-center gap-2 text-3xl mb-2">
 <span aria-hidden="true">{b.emoji}</span>
 </div>
 <h2 className="text-[24px] sm:text-[28px] font-black text-white leading-[1.1] tracking-tight pr-12">
 {b.modal.title}
 </h2>
 {b.modal.subtitle && (
 <p className="mt-1.5 text-[13.5px] text-yellow-300/90 font-semibold">{b.modal.subtitle}</p>
 )}
 </div>
 <div className="px-6 sm:px-7 py-5 overflow-y-auto flex-1 min-h-0">
 <p className="text-[14px] text-white/75 leading-relaxed whitespace-pre-line">
 {b.modal.intro}
 </p>
 <ul className="mt-5 space-y-2.5">
 {b.modal.points.map((p, i) => (
 <li key={i} className="flex items-start gap-2.5 text-[14px] text-white/85">
 <Check className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
 <span>{p}</span>
 </li>
 ))}
 </ul>
 </div>
 <div className="px-6 sm:px-7 pb-6 pt-4 border-t border-white/5 bg-black/30 flex-shrink-0">
 <button
 type="button"
 onClick={() => { setBenefitModalKey(null); openAuth('signup'); }}
 data-testid="benefit-modal-create"
 className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[14.5px] transition-all"
 >
 Create Free Account <ArrowRight className="w-4 h-4" />
 </button>
 <p className="mt-3 text-center text-[12px] text-white/55">
 Already have an account?{' '}
 <button
 type="button"
 onClick={() => { setBenefitModalKey(null); openAuth('login'); }}
 data-testid="benefit-modal-signin"
 className="text-yellow-300 hover:text-yellow-200 font-semibold underline underline-offset-2"
 >
 Sign In
 </button>
 </p>
 </div>
 </div>
 </div>,
 document.body,
 );
 })()}

 {/* ═══════════════ 5.5 SOCIAL SHARING (VERIFIED) ═══════════════ */}
 <section
 id="social-sharing"
 className="py-20 sm:py-24 border-b border-white/5 relative overflow-hidden"
 data-testid="social-sharing-section"
 >
 <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
 <div className="absolute top-1/3 left-[-15%] w-[34rem] h-[34rem] bg-yellow-500/[0.06] rounded-full blur-[140px]" />
 </div>

 <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
 {/* LEFT — narrative */}
 <div>
 <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.28em] mb-3">
 Verified social sharing
 </p>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.08]">
 Share once. Earn{' '}
 <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
 every time it counts.
 </span>
 </h2>
 <p className="mt-5 text-[14px] sm:text-[15px] text-white/60 leading-relaxed max-w-xl">
 Post your personal NoFee link on Instagram, TikTok, Facebook, X, WhatsApp or SMS.
 When we detect a real, verified share that leads to a signup or order, your
 rewards land in your dashboard automatically — no screenshots, no waiting.
 </p>

 <ul className="mt-7 space-y-3">
 {[
 { icon: BadgeCheck, label: 'Verified detection — no manual review needed' },
 { icon: ShieldCheck, label: 'Anti-fraud: no bots, no fake accounts, no self-referrals' },
 { icon: Coins, label: 'Points credited automatically when a friend signs up or a restaurant joins' },
 ].map(({ icon: Icon, label }) => (
 <li
 key={label}
 className="flex items-start gap-3 text-[14px] text-white/80"
 data-testid={`social-sharing-bullet-${label.toLowerCase().slice(0, 18).replace(/[^a-z]+/g, '-')}`}
 >
 <span className="mt-0.5 inline-flex w-7 h-7 rounded-lg border border-yellow-500/30 bg-yellow-500/[0.08] items-center justify-center shrink-0">
 <Icon className="w-3.5 h-3.5 text-yellow-400" strokeWidth={1.8} />
 </span>
 {label}
 </li>
 ))}
 </ul>

 <p className="mt-6 inline-flex items-center gap-2 text-[12px] text-white/40">
 <Lock className="w-3.5 h-3.5 text-yellow-400/70" />
 We never post anything on your behalf.
 </p>
 </div>

 {/* RIGHT — verified share cards */}
 <div
 className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#141414] via-[#0E0E0E] to-black p-5 sm:p-7 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.95)]"
 data-testid="social-sharing-preview"
 >
 <div className="flex items-center justify-between mb-5">
 <div>
 <div className="text-[10.5px] uppercase tracking-[0.2em] text-white/40 font-bold">
 Share & earn
 </div>
 <div className="font-['Outfit',_Inter,_sans-serif] text-lg font-medium text-white">
 Verified channels
 </div>
 </div>
 <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/[0.08]">
 <BadgeCheck className="w-3 h-3 text-emerald-300" />
 <span className="text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-bold">
 Verified
 </span>
 </div>
 </div>

 {/* Channel grid */}
 <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5">
 {[
 { name: 'Instagram', Icon: Instagram, pts: '+20', tone: 'pink' },
 { name: 'TikTok', Icon: Sparkles, pts: '+20', tone: 'cyan' },
 { name: 'Facebook', Icon: Facebook, pts: '+15', tone: 'blue' },
 { name: 'X / Twitter', Icon: Twitter, pts: '+15', tone: 'white' },
 { name: 'WhatsApp', Icon: MessageCircle, pts: '+10', tone: 'green' },
 { name: 'SMS', Icon: Share2, pts: '+10', tone: 'yellow' },
 ].map(({ name, Icon, pts }) => (
 <div
 key={name}
 data-testid={`social-share-channel-${name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
 className="group p-3.5 rounded-2xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] hover:border-yellow-500/30 transition-all"
 >
 <div className="flex items-center justify-between mb-2.5">
 <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:border-yellow-500/30 transition-colors">
 <Icon className="w-4 h-4 text-yellow-300/85" strokeWidth={1.75} />
 </div>
 <BadgeCheck className="w-3.5 h-3.5 text-emerald-300/85" />
 </div>
 <div className="text-[12.5px] font-semibold text-white tracking-tight leading-tight">
 {name}
 </div>
 <div className="mt-1 text-[11px] font-bold text-yellow-300 tabular-nums">
 {pts} pts / verified share
 </div>
 </div>
 ))}
 </div>

 {/* Footnote chip */}
 <div className="mt-5 flex items-center justify-between p-3 rounded-xl border border-yellow-500/20 bg-yellow-500/[0.04]">
 <div className="flex items-center gap-2.5">
 <div className="w-7 h-7 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
 <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
 </div>
 <div className="leading-tight">
 <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold">
 Bonus
 </div>
 <div className="text-[12.5px] text-white font-semibold">
 First verified share = +50 pts welcome boost
 </div>
 </div>
 </div>
 <BadgeCheck className="w-4 h-4 text-emerald-300" />
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* ═══════════════ 6. DASHBOARD PREVIEW ═══════════════ */}
 <section
 className="py-20 sm:py-24 border-b border-white/5"
 data-testid="dashboard-preview-section"
 >
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid lg:grid-cols-[1fr_1.15fr] gap-12 lg:gap-16 items-center">
 {/* LEFT — narrative */}
 <div>
 <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.28em] mb-3">
 Your dashboard
 </p>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.1]">
 Track everything you earn — in real time.
 </h2>
 <p className="mt-4 text-[15px] text-white/60 leading-relaxed max-w-xl">
 The moment you sign up, your personal NoFee dashboard goes live with your referral
 code, credits balance, and a full history of every reward you earn.
 </p>

 <ul className="mt-7 space-y-3">
 {[
 { icon: Tag, label: 'Your personal referral code, ready to share' },
 { icon: Coins, label: 'Live gift-credit balance — redeem on any order' },
 { icon: Gift, label: 'Activity ledger — every credit earned, with timestamps' },
 { icon: Users, label: 'Friends invited & restaurants referred' },
 { icon: Receipt, label: 'Transparent ledger of every reward earned' },
 ].map(({ icon: Icon, label }) => (
 <li key={label} className="flex items-start gap-3 text-[14px] text-white/80">
 <span className="mt-0.5 inline-flex w-7 h-7 rounded-lg border border-yellow-500/30 bg-yellow-500/[0.08] items-center justify-center shrink-0">
 <Icon className="w-3.5 h-3.5 text-yellow-400" strokeWidth={1.8} />
 </span>
 {label}
 </li>
 ))}
 </ul>

 <p className="mt-7 text-[12.5px] text-white/40 leading-relaxed">
 What you see on the right is the actual dashboard — not a marketing mockup.
 </p>
 </div>

 {/* RIGHT — premium mock dashboard */}
 <div
 className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#141414] via-[#0E0E0E] to-black p-5 sm:p-7 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.95)]"
 data-testid="dashboard-preview-mock"
 >
 {/* Header */}
 <div className="flex items-center justify-between mb-5">
 <div>
 <div className="text-[10.5px] uppercase tracking-[0.2em] text-white/40 font-bold">
 Hi, Maria
 </div>
 <div className="font-['Outfit',_Inter,_sans-serif] text-lg font-medium text-white">
 NoFee Rewards
 </div>
 </div>
 <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-yellow-500/40 bg-yellow-500/[0.08]">
 <Sparkles className="w-3 h-3 text-yellow-400" />
 <span className="text-[10px] uppercase tracking-[0.16em] text-yellow-300 font-bold">
 Founding
 </span>
 </div>
 </div>

 {/* Big metric */}
 <div className="p-5 rounded-2xl bg-gradient-to-br from-yellow-500/[0.08] via-amber-500/[0.05] to-transparent border border-yellow-500/20 mb-3">
 <div className="text-[10.5px] uppercase tracking-[0.2em] text-yellow-300/85 font-bold">
 Gift credit balance
 </div>
 <div className="mt-1 font-['Outfit',_Inter,_sans-serif] text-4xl sm:text-5xl font-semibold tracking-[-0.03em] bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
 $42.50
 </div>
 <div className="mt-1 text-[12px] text-white/45">850 points · 20 pts = $1</div>
 </div>

 {/* Stats grid */}
 <div className="grid grid-cols-3 gap-2 mb-3">
 {[
 { v: '7', l: 'Invited' },
 { v: '2', l: 'Restaurants' },
 { v: '12', l: 'Rewards' },
 ].map((s) => (
 <div
 key={s.l}
 className="p-3 rounded-xl border border-white/8 bg-white/[0.02] text-center"
 >
 <div className="font-['Outfit',_Inter,_sans-serif] text-xl font-medium text-white">
 {s.v}
 </div>
 <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-bold mt-0.5">
 {s.l}
 </div>
 </div>
 ))}
 </div>

 {/* Referral code chip */}
 <div className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/[0.025] mb-3">
 <div className="flex items-center gap-2.5">
 <div className="w-8 h-8 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
 <Tag className="w-3.5 h-3.5 text-yellow-400" />
 </div>
 <div>
 <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-bold">
 Your code
 </div>
 <div className="text-[13px] font-bold text-white tabular-nums">NOFI-MARIA8</div>
 </div>
 </div>
 <span className="text-[11px] text-yellow-300/90 font-bold">Copy</span>
 </div>

 {/* Activity rows */}
 <div className="space-y-1.5">
 {[
 { icon: Users, label: 'Friend signup', sub: '@nora', amt: '+10 pts' },
 { icon: Store, label: 'Restaurant joined', sub: 'Bella Italia', amt: '+30 pts' },
 { icon: Share2, label: 'Verified share', sub: 'Instagram', amt: '+20 pts' },
 ].map((r) => (
 <div
 key={r.label}
 className="flex items-center justify-between p-2.5 rounded-xl border border-white/5 bg-white/[0.015]"
 >
 <div className="flex items-center gap-2.5">
 <div className="w-7 h-7 rounded-lg bg-white/[0.04] flex items-center justify-center">
 <r.icon className="w-3.5 h-3.5 text-yellow-400/85" />
 </div>
 <div className="leading-tight">
 <div className="text-[12px] font-medium text-white">{r.label}</div>
 <div className="text-[10.5px] text-white/40">{r.sub}</div>
 </div>
 </div>
 <div className="text-[11.5px] font-bold text-emerald-300 tabular-nums">
 {r.amt}
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* ═══════════════ 7. FOUNDING MEMBER BADGE ═══════════════
 iter89 (Feb 2026): the old Bronze/Gold/Elite ladder section
 was retired in favour of the Gift Card Credits economy.
 The Founding badge stays as an aspirational early-believer
 recognition (no tiers, no Ambassador wording). */}
 <section
 className="py-20 sm:py-24 border-b border-white/5 bg-gradient-to-b from-transparent via-black to-[#0A0A0A]"
 data-testid="founding-badge-section"
 >
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-2xl mx-auto mb-10">
 <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.28em] mb-3">
 Founding recognition
 </p>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-tight">
 A permanent badge for early believers.
 </h2>
 <p className="mt-4 text-[15px] text-white/55 leading-relaxed">
 An exclusive Founding Member badge for the first 100 customers who join — limited recognition that stays with you for life.
 </p>
 </div>

 {/* Founding badge — aspirational, no tier ladder */}
 <div
 className="mt-6 sm:mt-8 p-6 sm:p-7 rounded-3xl border border-yellow-500/25 bg-gradient-to-r from-yellow-500/[0.05] via-amber-500/[0.04] to-yellow-500/[0.05] flex flex-col sm:flex-row items-start sm:items-center gap-5 max-w-4xl mx-auto"
 data-testid="founding-member"
 >
 <div className="w-14 h-14 rounded-2xl border border-yellow-500/40 bg-black/40 flex items-center justify-center shrink-0">
 <Trophy className="w-6 h-6 text-yellow-400" strokeWidth={1.5} />
 </div>
 <div className="flex-1">
 <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-yellow-300 mb-1">
 Founding Member
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-lg sm:text-xl font-medium tracking-tight text-white">
 Limited recognition for our earliest community members.
 </h3>
 <p className="mt-2 text-[13px] text-white/55 leading-relaxed">
 Awarded automatically to the first 100 members. Permanent badge, lifetime perks,
 priority access to every NoFee launch.
 </p>
 </div>
 </div>
 </div>
 </section>

 {/* ═══════════════ 8. RESTAURANT VALUE ═══════════════ */}
 <section
 className="py-20 sm:py-24 border-b border-white/5 relative overflow-hidden"
 data-testid="restaurant-value-section"
 >
 <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
 <div className="absolute top-1/4 right-[-10%] w-[36rem] h-[36rem] bg-yellow-500/[0.06] rounded-full blur-[140px]" />
 </div>

 <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-2xl mx-auto mb-14">
 <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.28em] mb-3">
 Why restaurants love NoFee
 </p>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-tight">
 Every referral helps a kitchen{' '}
 <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
 keep 100%
 </span>{' '}
 of their revenue.
 </h2>
 <p className="mt-4 text-[15px] text-white/55 leading-relaxed">
 Big delivery apps charge restaurants 25–35% per order. NoFee charges zero. You're not
 just earning rewards — you're directly fueling local businesses.
 </p>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
 {[
 {
 icon: DollarSign,
 metric: '0%',
 title: 'Commission',
 desc: 'Restaurants keep every dollar — no marketplace fee, no commission tier.',
 },
 {
 icon: Zap,
 metric: 'Direct',
 title: 'Payouts',
 desc: 'Stripe direct to restaurant accounts. No middleman holding funds.',
 },
 {
 icon: TrendingUp,
 metric: '−20%',
 title: 'Avg. customer prices',
 desc: 'Without commission, restaurants pass savings on. Cheaper for everyone.',
 },
 {
 icon: Award,
 metric: 'Real',
 title: 'Growth',
 desc: 'Members bring real local customers — not throw-away discount hunters.',
 },
 ].map(({ icon: Icon, metric, title, desc }) => (
 <div
 key={title}
 data-testid={`rest-value-${title.toLowerCase().replace(/\s+/g, '-')}`}
 className="group p-6 sm:p-7 rounded-2xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] hover:border-yellow-500/30 transition-all hover:-translate-y-0.5"
 >
 <div className="w-11 h-11 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mb-5 group-hover:bg-yellow-500/15 transition-colors">
 <Icon className="w-5 h-5 text-yellow-400" strokeWidth={1.75} />
 </div>
 <div className="font-['Outfit',_Inter,_sans-serif] text-3xl font-semibold tracking-[-0.02em] bg-gradient-to-b from-yellow-200 to-yellow-500 bg-clip-text text-transparent">
 {metric}
 </div>
 <h3 className="mt-1 font-['Outfit',_Inter,_sans-serif] text-base font-medium text-white tracking-tight">
 {title}
 </h3>
 <p className="mt-2 text-[13px] text-white/55 leading-relaxed">{desc}</p>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* ═══════════════ 8.5 IMPORTANT / FAIR USE ═══════════════ */}
 <section
 className="py-16 sm:py-20 border-b border-white/5 bg-gradient-to-b from-black via-[#0A0A0A] to-black"
 data-testid="fair-use-section"
 >
 <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-2xl mx-auto mb-10">
 <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.28em] mb-3">
 How credits work · The fine print
 </p>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl font-semibold tracking-[-0.03em] leading-tight">
 Honest, transparent, and 100% food-first.
 </h2>
 <p className="mt-4 text-[14px] sm:text-[15px] text-white/55 leading-relaxed">
 A few important things to know so you get the most out of NoFee Rewards.
 </p>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
 <div
 data-testid="fair-use-card-redeemable"
 className="p-6 rounded-2xl border border-white/8 bg-white/[0.02]"
 >
 <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mb-4">
 <Store className="w-4.5 h-4.5 text-yellow-400" strokeWidth={1.75} />
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-base font-medium text-white tracking-tight">
 Usable at participating restaurants
 </h3>
 <p className="mt-2 text-[13px] text-white/55 leading-relaxed">
 Gift credits redeem at any restaurant on NoFee that accepts gift redemptions.
 Show your 8-character code at checkout — that's it.
 </p>
 </div>

 <div
 data-testid="fair-use-card-not-cash"
 className="p-6 rounded-2xl border border-amber-500/20 bg-amber-500/[0.04]"
 >
 <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
 <Lock className="w-4.5 h-4.5 text-amber-300" strokeWidth={1.75} />
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-base font-medium text-white tracking-tight">
 Not cash. Not withdrawable.
 </h3>
 <p className="mt-2 text-[13px] text-white/65 leading-relaxed">
 Gift credits have no cash value. They cannot be transferred, sold, or
 withdrawn to a bank, or Zelle account. They live inside NoFee, for food.
 </p>
 </div>

 <div
 data-testid="fair-use-card-fair-share"
 className="p-6 rounded-2xl border border-white/8 bg-white/[0.02]"
 >
 <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mb-4">
 <ShieldCheck className="w-4.5 h-4.5 text-yellow-400" strokeWidth={1.75} />
 </div>
 <h3 className="font-['Outfit',_Inter,_sans-serif] text-base font-medium text-white tracking-tight">
 Fair sharing only
 </h3>
 <p className="mt-2 text-[13px] text-white/55 leading-relaxed">
 Real referrals only — no bot farms, fake accounts, or self-referrals.
 Verified shares keep the program valuable for everyone.
 </p>
 </div>
 </div>

 <p className="mt-8 text-center text-[12.5px] text-white/40 leading-relaxed">
 Full details:{' '}
 <Link
 to="/rewards-terms"
 className="text-yellow-300 hover:text-yellow-200 font-medium underline-offset-4 hover:underline"
 data-testid="fair-use-terms-link"
 >
 NoFee Rewards Terms
 </Link>
.
 </p>
 </div>
 </section>

 {/* ═══════════════ 9. FINAL CTA ═══════════════ */}
 <section
 className="relative py-24 sm:py-32 px-4 text-center overflow-hidden"
 data-testid="final-cta-section"
 >
 <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
 <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] bg-yellow-500/[0.08] rounded-full blur-[140px]" />
 </div>

 <div className="relative max-w-3xl mx-auto">
 <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl border border-yellow-500/35 bg-yellow-500/[0.08] mx-auto mb-7">
 <Crown className="w-7 h-7 text-yellow-400" strokeWidth={1.5} />
 </div>
 <h2 className="font-['Outfit',_Inter,_sans-serif] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[1.05]">
 Ready to start{' '}
 <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
 earning rewards
 </span>
?
 </h2>
 <p className="mt-5 text-[15px] sm:text-base text-white/60 max-w-xl mx-auto leading-relaxed">
 Free forever. No credit card. Unlimited referrals. One account — everything in one place.
 </p>

 {isCustomerLoggedIn? (
 // One-account rule: the user is already a NoFee customer — no
 // second signup. Route them directly to their loyalty Rewards.
 <div className="mt-10 flex flex-col items-center gap-3">
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/[0.08] text-[11px] uppercase tracking-[0.18em] text-emerald-300 font-bold">
 <Check className="w-3.5 h-3.5" /> You're already a NoFee member
 </div>
 <Link
 to="/account"
 data-testid="final-cta-button"
 className="group inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
 >
 <Sparkles className="w-4 h-4" /> Continue to my NoFee
 <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
 </Link>
 <p className="text-[12px] text-white/40 max-w-sm">
 One account. Your customer account already includes Rewards — nothing else to set up.
 </p>
 </div>
 ): ambassador? (
 <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
 <Link
 to="/rewards/dashboard"
 data-testid="final-cta-button"
 className="inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
 >
 Continue to Dashboard <ArrowRight className="w-4 h-4" />
 </Link>
 </div>
 ): (
 <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
 <button
 type="button"
 onClick={() => openAuth('signup')}
 data-testid="final-cta-button"
 className="group inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
 >
 <Sparkles className="w-4 h-4" /> Start Earning Rewards
 <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
 </button>
 <button
 type="button"
 onClick={() => openAuth('login')}
 data-testid="final-cta-signin"
 className="inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl border border-white/15 hover:border-yellow-500/50 bg-white/[0.03] hover:bg-white/[0.06] text-white font-bold text-[15px] transition-all"
 >
 <LogIn className="w-4 h-4" /> Sign in
 </button>
 </div>
 )}

 <div className="mt-10 inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-white/40">
 <span className="inline-flex items-center gap-1.5">
 <Wallet className="w-3.5 h-3.5 text-yellow-400/80" /> Real gift credits
 </span>
 <span className="inline-flex items-center gap-1.5">
 <ShieldCheck className="w-3.5 h-3.5 text-yellow-400/80" /> Fair & transparent
 </span>
 <span className="inline-flex items-center gap-1.5">
 <Share2 className="w-3.5 h-3.5 text-yellow-400/80" /> Verified shares
 </span>
 </div>
 </div>
 </section>

 <footer className="border-t border-white/5 py-8 text-center text-[12px] text-white/30">
 © NoFee.restaurant · More control. More freedom. More results.{' '}
 <span className="text-yellow-400">No fees.</span>
 </footer>

 {/* Sticky mobile CTA */}
 {!ambassador && <StickyMobileCTA visible={stickyVisible} onJoin={() => openAuth('signup')} />}

 <CustomerAuthModal open={authOpen} initialMode={authMode} onClose={closeAuth} />
 </div>
 );
};

const RewardsLanding = () => (
 <LanguageProvider>
 <RewardsLandingInner />
 </LanguageProvider>
);

export default RewardsLanding;
